import { DatePicker } from "./DatePicker.js";
import { Sunrise, Sunset, AlertTriangle } from "lucide-react";

const SINGLE_DATE_TYPES = new Set(["COMP_OFF"]);
const HALF_DAY_TYPES    = new Set(["ANNUAL", "SICK", "CASUAL"]);

function workingDays(start: string, end: string): number {
  if (!start || !end) return 0;
  let count = 0;
  const cur  = new Date(start + "T00:00:00");
  const last = new Date(end   + "T00:00:00");
  while (cur <= last) {
    const d = cur.getDay();
    if (d !== 0 && d !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

function isWeekend(dateStr: string): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr + "T00:00:00").getDay();
  return d === 0 || d === 6;
}

function spansWeekend(start: string, end: string): boolean {
  if (!start || !end) return false;
  const cur  = new Date(start + "T00:00:00");
  const last = new Date(end   + "T00:00:00");
  while (cur <= last) {
    const d = cur.getDay();
    if (d === 0 || d === 6) return true;
    cur.setDate(cur.getDate() + 1);
  }
  return false;
}

interface Props {
  leaveType:     string;
  startDate:     string;
  endDate:       string;
  halfDay:       boolean;
  halfDayPeriod: "MORNING" | "AFTERNOON" | "";
  onStartDate:   (v: string) => void;
  onEndDate:     (v: string) => void;
  onHalfDay:     (v: boolean) => void;
  onHalfDayPeriod: (v: "MORNING" | "AFTERNOON") => void;
}

const inp: React.CSSProperties = {
  width: "100%", padding: "8px 10px",
  border: "1.5px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: 13, outline: "none",
  boxSizing: "border-box", background: "var(--color-surface-0)",
};

const label: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: "var(--color-surface-600)", marginBottom: 4, display: "block",
};

export function LeaveDatePicker({
  leaveType, startDate, endDate, halfDay, halfDayPeriod,
  onStartDate, onEndDate, onHalfDay, onHalfDayPeriod,
}: Props) {
  const isSingleDate  = SINGLE_DATE_TYPES.has(leaveType);
  const supportsHalf  = HALF_DAY_TYPES.has(leaveType);

  const effectiveEnd  = isSingleDate || halfDay ? startDate : endDate;
  const days          = halfDay ? 0.5 : workingDays(startDate, effectiveEnd);
  const weekend       = !halfDay && spansWeekend(startDate, effectiveEnd);
  const startIsWeekend = isWeekend(startDate);

  function handleStartChange(v: string) {
    onStartDate(v);
    if (isSingleDate || halfDay) onEndDate(v);
  }

  function handleHalfDayToggle(checked: boolean) {
    onHalfDay(checked);
    if (checked) {
      onEndDate(startDate);
      if (!halfDayPeriod) onHalfDayPeriod("MORNING");
    }
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={label}>Start Date</label>
          <DatePicker style={inp} value={startDate} onChange={handleStartChange} />
        </div>

        {!isSingleDate && !halfDay && (
          <div style={{ flex: 1 }}>
            <label style={label}>End Date</label>
            <DatePicker style={inp} value={endDate} min={startDate} onChange={onEndDate} />
          </div>
        )}
      </div>

      {/* Half-day toggle */}
      {supportsHalf && !isSingleDate && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: 10, color: "var(--color-surface-700)" }}>
          <input
            type="checkbox"
            checked={halfDay}
            onChange={(e) => handleHalfDayToggle(e.target.checked)}
          />
          Half day
        </label>
      )}

      {/* AM/PM picker when half-day */}
      {halfDay && (
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          {(["MORNING", "AFTERNOON"] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onHalfDayPeriod(p)}
              style={{
                flex: 1, padding: "8px 0", fontSize: 12, fontWeight: 600,
                border: halfDayPeriod === p ? "2px solid var(--color-brand-500)" : "1.5px solid var(--color-surface-200)",
                borderRadius: 8, cursor: "pointer",
                background: halfDayPeriod === p ? "var(--color-brand-50, #eff6ff)" : "var(--color-surface-0)",
                color: halfDayPeriod === p ? "var(--color-brand-600)" : "var(--color-surface-700)",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                {p === "MORNING" ? <Sunrise size={14} /> : <Sunset size={14} />}
                {p === "MORNING" ? "Morning" : "Afternoon"}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Live counter */}
      {startDate && (
        <div style={{
          fontSize: 13, padding: "8px 12px",
          background: startIsWeekend ? "var(--color-error-bg-subtle)" : days === 0 ? "var(--color-error-bg-subtle)" : "var(--color-success-bg-subtle)",
          borderRadius: 8, color: startIsWeekend || days === 0 ? "var(--color-error)" : "var(--color-success-fg)",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          {startIsWeekend ? (
            <><AlertTriangle size={14} /> This date falls on a weekend — no working days</>
          ) : halfDay ? (
            `0.5 working day · half day (${halfDayPeriod === "MORNING" ? "morning" : "afternoon"})`
          ) : days === 0 ? (
            <><AlertTriangle size={14} /> No working days in selected range</>
          ) : (
            <>
              <strong>{days} working {days === 1 ? "day" : "days"}</strong>
              {weekend && <span style={{ color: "#4b7a55", fontSize: 11 }}>· Weekends &amp; public holidays not counted</span>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
